import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical, Plus, Trash2 } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import GenerateDocumentButton from '../components/GenerateDocumentButton'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineDate,
  InlineSelect,
  InlineStatus,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

// ─────────────────── Types ───────────────────
type Partner = {
  id: number
  code: string
  name: string
  partner_type: 'CUSTOMER' | 'VENDOR' | 'BOTH' | 'FREELANCER'
  // legal entity
  legal_name: string
  legal_form: string
  registration_number: string
  registration_authority: string
  registration_date: string | null
  tax_id: string
  vat_id: string
  legal_representative: string
  // contact
  email: string
  phone: string
  website: string
  contact_person_name: string
  contact_person_email: string
  contact_person_phone: string
  // registered address
  address_line1: string
  address_line2: string
  city: string
  postal_code: string
  country_code: string
  hq: string
  // finance
  currency_code: string
  is_active: boolean
  notes: string
}

type VendorContract = {
  id: number
  contract_number: string
  partner: number
  title: string
  status: 'DRAFT' | 'ACTIVE' | 'EXPIRED' | 'TERMINATED'
  start_date: string | null
  end_date: string | null
  default_rate: string
  rate_unit: 'HOUR' | 'DAY'
  currency_code: string
}

// ─────────────────── Constants ───────────────────
const TYPE_BADGE: Record<Partner['partner_type'], string> = {
  CUSTOMER: 'bg-blue-100 text-blue-800',
  VENDOR: 'bg-amber-100 text-amber-800',
  BOTH: 'bg-purple-100 text-purple-800',
  FREELANCER: 'bg-teal-100 text-teal-800',
}

const TYPE_LABEL: Record<Partner['partner_type'], string> = {
  CUSTOMER: 'Customer',
  VENDOR: 'Vendor',
  BOTH: 'Both',
  FREELANCER: 'Freelancer',
}

const ACTIVE_COLOR: Record<'ACTIVE' | 'INACTIVE', string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  INACTIVE: 'bg-gray-100 text-gray-600',
}

const VC_STATUS_COLOR: Record<VendorContract['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-green-100 text-green-800',
  EXPIRED: 'bg-amber-100 text-amber-800',
  TERMINATED: 'bg-red-100 text-red-700',
}

type PortalStatus = 'active' | 'pending' | 'inactive' | 'no_account' | 'no_email'

type VendorContact = {
  id: number
  partner: number
  partner_name: string
  person: number
  person_name: string
  person_email: string
  role: string
  is_primary: boolean
  is_portal_active: boolean
  portal_status: PortalStatus
  notes: string
  created_at: string
  updated_at: string
}

type PersonOption = { id: number; full_name: string; personal_email: string }

type TabKey = 'details' | 'contacts' | 'contracts' | 'files'

// ─────────────────── Page ───────────────────
export default function FinancePartnerDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const partnerId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const partnerQ = useQuery({
    queryKey: ['finance', 'partner', partnerId],
    queryFn: async () => (await api.get<Partner>(`/partners/${partnerId}/`)).data,
    enabled: !!partnerId,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Partner>) =>
      (await api.patch(`/partners/${partnerId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'partner', partnerId] })
      qc.invalidateQueries({ queryKey: ['finance', 'partners'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/partners/${partnerId}/`),
    onSuccess: () => {
      toast.success('Partner deleted')
      qc.invalidateQueries({ queryKey: ['finance', 'partners'] })
      navigate('/admin/partners')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (partnerQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (partnerQ.isError || !partnerQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Partner not found.</p>
          <Link to="/admin/partners" className="text-blue-600 hover:underline">← Back to partners</Link>
        </div>
      </AdminLayout>
    )
  }

  const partner = partnerQ.data
  const showContracts = partner.partner_type === 'VENDOR'
    || partner.partner_type === 'BOTH'
    || partner.partner_type === 'FREELANCER'

  const save = (payload: Partial<Partner>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/partners" className="text-muted-foreground hover:text-foreground hover:underline">← Partners</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{partner.code}</div>
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={partner.name} onSave={(v) => save({ name: v })} />
            </h2>
            <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${TYPE_BADGE[partner.partner_type]}`}>
                {TYPE_LABEL[partner.partner_type]}
              </span>
              {partner.country_code && <span> · <span className="font-mono">{partner.country_code}</span></span>}
              {partner.currency_code && <span> · <span className="font-mono">{partner.currency_code}</span></span>}
              {partner.website && (
                <>
                  {' · '}
                  <a href={partner.website} target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 hover:underline">{partner.website}</a>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <GenerateDocumentButton
              sourceType="partners.Partner"
              sourceId={partner.id}
              label="Generate document…"
            />
            <InlineStatus
              value={partner.is_active ? 'ACTIVE' : 'INACTIVE'}
              colorMap={ACTIVE_COLOR}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
              onSave={(v) => save({ is_active: v === 'ACTIVE' })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(
                    `partner ${partner.code} (${partner.name})`,
                    () => deleteMut.mutate(),
                  )}
                  className="text-destructive"
                >
                  Delete partner
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          <button
            onClick={() => setTab('details')}
            className={`px-4 py-2 -mb-px border-b-2 ${
              tab === 'details' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setTab('contacts')}
            className={`px-4 py-2 -mb-px border-b-2 ${
              tab === 'contacts' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Contacts
          </button>
          {showContracts && (
            <button
              onClick={() => setTab('contracts')}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === 'contracts' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Vendor contracts
            </button>
          )}
          <button
            onClick={() => setTab('files')}
            className={`px-4 py-2 -mb-px border-b-2 ${
              tab === 'files' ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Files
          </button>
        </nav>
      </div>

      {tab === 'details' && <DetailsTab partner={partner} onSave={save} />}
      {tab === 'contacts' && <ContactsTab partnerId={partner.id} />}
      {tab === 'contracts' && showContracts && <ContractsTab partnerId={partner.id} />}
      {tab === 'files' && <FilesTab parent="finance.partner" parentId={partnerId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  partner, onSave,
}: {
  partner: Partner
  onSave: (payload: Partial<Partner>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">

      {/* ── Identity ── */}
      <Section title="Identity">
        <Row label="Code">
          <InlineText value={partner.code} onSave={(v) => onSave({ code: v })} className="font-mono" />
        </Row>
        <Row label="Trading name">
          <InlineText value={partner.name} onSave={(v) => onSave({ name: v })} />
        </Row>
        <Row label="Partner type">
          <InlineSelect
            value={partner.partner_type}
            displayLabel={
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${TYPE_BADGE[partner.partner_type]}`}>
                {TYPE_LABEL[partner.partner_type]}
              </span>
            }
            options={[
              { value: 'CUSTOMER', label: 'Customer' },
              { value: 'VENDOR', label: 'Vendor' },
              { value: 'BOTH', label: 'Both (customer & vendor)' },
              { value: 'FREELANCER', label: 'Freelancer' },
            ]}
            onSave={(v) => onSave({ partner_type: v as Partner['partner_type'] })}
            helper="Drives where this partner appears as a candidate (invoice customers / bill vendors / freelancer pool)."
          />
        </Row>
      </Section>

      {/* ── Legal entity ── */}
      <Section title="Legal entity">
        <Row label="Legal name">
          <InlineText
            value={partner.legal_name}
            onSave={(v) => onSave({ legal_name: v })}
            placeholder="Full registered name"
            helper="Official name as registered — used on invoices and contracts."
          />
        </Row>
        <Row label="Legal form">
          <InlineText
            value={partner.legal_form}
            onSave={(v) => onSave({ legal_form: v })}
            placeholder="e.g. GmbH, SRL, Ltd, SA, LLC"
          />
        </Row>
        <Row label="Registration #">
          <InlineText
            value={partner.registration_number}
            onSave={(v) => onSave({ registration_number: v })}
            className="font-mono"
            placeholder="Trade / company register number"
          />
        </Row>
        <Row label="Registration authority">
          <InlineText
            value={partner.registration_authority}
            onSave={(v) => onSave({ registration_authority: v })}
            placeholder="e.g. ONRC Bucuresti, Amtsgericht Berlin, Companies House"
            helper="Registry that issued the registration number."
          />
        </Row>
        <Row label="Registered on">
          <InlineDate
            value={partner.registration_date}
            onSave={(v) => onSave({ registration_date: v })}
            // Registration dates are routinely years or decades back, so pick
            // the year and month directly. Capped at the current year — a
            // company cannot have been registered in the future.
            captionLayout="dropdown"
            fromYear={1900}
            toYear={new Date().getFullYear()}
          />
        </Row>
        <Row label="Tax ID">
          <InlineText
            value={partner.tax_id}
            onSave={(v) => onSave({ tax_id: v })}
            className="font-mono"
            placeholder="Fiscal / tax identification number"
          />
        </Row>
        <Row label="VAT ID">
          <InlineText
            value={partner.vat_id}
            onSave={(v) => onSave({ vat_id: v })}
            className="font-mono"
            placeholder="e.g. DE123456789"
            helper="EU VAT or equivalent — may differ from Tax ID."
          />
        </Row>
        <Row label="Legal representative">
          <InlineText
            value={partner.legal_representative}
            onSave={(v) => onSave({ legal_representative: v })}
            placeholder="Name of the authorised signatory"
          />
        </Row>
      </Section>

      {/* ── Registered address ── */}
      <Section title="Registered address">
        <Row label="Address line 1">
          <InlineText value={partner.address_line1} onSave={(v) => onSave({ address_line1: v })} />
        </Row>
        <Row label="Address line 2">
          <InlineText value={partner.address_line2} onSave={(v) => onSave({ address_line2: v })} />
        </Row>
        <Row label="City">
          <InlineText value={partner.city} onSave={(v) => onSave({ city: v })} />
        </Row>
        <Row label="Postal code">
          <InlineText value={partner.postal_code} onSave={(v) => onSave({ postal_code: v })} />
        </Row>
        <Row label="Country (ISO 2)">
          <InlineText
            value={partner.country_code}
            onSave={(v) => onSave({ country_code: v.toUpperCase().slice(0, 2) })}
            className="font-mono"
            placeholder="e.g. DE"
          />
        </Row>
        <Row label="HQ">
          <InlineText
            value={partner.hq}
            onSave={(v) => onSave({ hq: v })}
            placeholder="Principal place of business if different from above"
          />
        </Row>
      </Section>

      {/* ── Contact ── */}
      <Section title="Contact">
        <Row label="Email">
          <InlineText value={partner.email} onSave={(v) => onSave({ email: v })} placeholder="name@company.com" />
        </Row>
        <Row label="Phone">
          <InlineText value={partner.phone} onSave={(v) => onSave({ phone: v })} />
        </Row>
        <Row label="Website">
          <InlineText value={partner.website} onSave={(v) => onSave({ website: v })} placeholder="https://…" />
        </Row>
      </Section>

      {/* ── Primary contact ── */}
      <Section title="Primary contact">
        <Row label="Name">
          <InlineText
            value={partner.contact_person_name}
            onSave={(v) => onSave({ contact_person_name: v })}
            placeholder="Account manager / invoicing contact"
          />
        </Row>
        <Row label="Email">
          <InlineText
            value={partner.contact_person_email}
            onSave={(v) => onSave({ contact_person_email: v })}
            placeholder="name@company.com"
          />
        </Row>
        <Row label="Phone">
          <InlineText value={partner.contact_person_phone} onSave={(v) => onSave({ contact_person_phone: v })} />
        </Row>
      </Section>

      {/* ── Finance defaults ── */}
      <Section title="Finance defaults">
        <Row label="Default currency">
          <InlineText
            value={partner.currency_code}
            onSave={(v) => onSave({ currency_code: v.toUpperCase().slice(0, 3) })}
            className="font-mono"
            placeholder="e.g. EUR"
            helper="3-letter ISO code used as default on new invoices, bills, and vendor contracts."
          />
        </Row>
      </Section>

      {/* ── Notes ── */}
      <Section title="Notes">
        <Row label="Notes">
          <InlineText
            value={partner.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            placeholder="Internal notes about this partner"
          />
        </Row>
      </Section>

    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Vendor contracts tab ───────────────────
// Read-only here. Creating / editing happens on the Vendor Contract record page.

function ContractsTab({ partnerId }: { partnerId: number }) {
  const navigate = useNavigate()
  const { data: contracts = [], isLoading } = useQuery({
    queryKey: ['finance', 'vendor-contracts', { partner: partnerId }],
    queryFn: async () => (await api.get<VendorContract[]>(`/partners/vendor-contracts/?partner=${partnerId}`)).data,
  })

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground max-w-2xl">
        Vendor contracts where this partner is the counterparty. Open a contract to edit rates, validity, and assignments. New contracts are created on the Vendor Contracts page.
      </p>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : contracts.length === 0 ? (
        <p className="text-muted-foreground text-sm">No vendor contracts yet.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Number</Th>
                <Th>Title</Th>
                <Th>Period</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-border hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/partners/vendor-contracts/${c.id}`)}
                >
                  <Td className="font-mono text-xs">{c.contract_number}</Td>
                  <Td className="font-medium">{c.title || <Dash />}</Td>
                  <Td className="text-right font-mono text-xs">
                    {Number(c.default_rate).toFixed(2)} {c.currency_code} / {c.rate_unit === 'DAY' ? 'day' : 'hour'}
                  </Td>
                  <Td className="text-xs">
                    {c.start_date || <Dash />} → {c.end_date || <span className="text-muted-foreground">open</span>}
                  </Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${VC_STATUS_COLOR[c.status]}`}>
                      {c.status}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─────────────────── Contacts tab ───────────────────

const PORTAL_STATUS_CFG: Record<PortalStatus, { label: string; cls: string; tip: string }> = {
  active:     { label: 'Active',      cls: 'bg-green-100 text-green-800', tip: 'Portal access granted' },
  pending:    { label: 'Invite sent', cls: 'bg-amber-100 text-amber-800', tip: 'Welcome email sent — waiting for password to be set' },
  inactive:   { label: 'Inactive',    cls: 'bg-red-100 text-red-700',     tip: 'Account deactivated' },
  no_account: { label: 'No account',  cls: 'bg-gray-100 text-gray-600',  tip: 'No portal account yet' },
  no_email:   { label: 'No email',    cls: 'bg-gray-100 text-gray-500',  tip: 'No email address — cannot provision an account' },
}

function ContactPortalBadge({ status }: { status: PortalStatus }) {
  const { label, cls, tip } = PORTAL_STATUS_CFG[status]
  return <span className={`text-xs px-2 py-0.5 rounded font-semibold ${cls}`} title={tip}>{label}</span>
}

const NONE = '__none__'

function ContactsTab({ partnerId }: { partnerId: number }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const [adding, setAdding] = useState(false)
  const [personId, setPersonId] = useState<string>(NONE)
  const [role, setRole] = useState('')
  const [isPrimary, setIsPrimary] = useState(false)

  const contactsQ = useQuery({
    queryKey: ['partners', 'contacts', partnerId],
    queryFn: async () =>
      (await api.get<VendorContact[]>(`/partners/contacts/?partner=${partnerId}`)).data,
  })

  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<PersonOption[]>('/hr/persons/')).data,
  })

  const addMut = useMutation({
    mutationFn: async () => api.post('/partners/contacts/', {
      partner: partnerId,
      person: Number(personId),
      role,
      is_primary: isPrimary,
      is_portal_active: true,
    }),
    onSuccess: () => {
      toast.success('Contact added')
      qc.invalidateQueries({ queryKey: ['partners', 'contacts', partnerId] })
      setAdding(false)
      setPersonId(NONE)
      setRole('')
      setIsPrimary(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const togglePortalMut = useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) =>
      api.patch(`/partners/contacts/${id}/`, { is_portal_active: active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partners', 'contacts', partnerId] }),
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/partners/contacts/${id}/`),
    onSuccess: () => {
      toast.success('Contact removed')
      qc.invalidateQueries({ queryKey: ['partners', 'contacts', partnerId] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const contacts = contactsQ.data ?? []
  const persons = personsQ.data ?? []

  // Build options, excluding already-added persons
  const addedIds = new Set(contacts.map((c) => c.person))
  const personOptions = [
    { value: NONE, label: '— select person —' },
    ...persons
      .filter((p) => !addedIds.has(p.id))
      .map((p) => ({
        value: String(p.id),
        label: p.full_name,
        hint: p.personal_email || undefined,
      })),
  ]

  return (
    <section className="bg-card rounded-lg shadow p-6">
      {dialog}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-foreground">Contacts</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            People at this partner with portal access to shared invoices and documents.
          </p>
        </div>
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="h-3 w-3 mr-1" /> Add contact
          </Button>
        )}
      </div>

      {adding && (
        <div className="mb-5 p-4 border border-border rounded-lg bg-muted/30 space-y-3">
          <p className="text-sm font-medium text-foreground">Add a contact</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                Person *
              </label>
              <SearchableSelect
                value={personId}
                onChange={setPersonId}
                options={personOptions}
                placeholder="Search people…"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                Role at partner
              </label>
              <Input
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="e.g. Account Manager"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <input
              id="vc-primary"
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="vc-primary" className="text-muted-foreground">
              Mark as primary contact (used for invoicing)
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setAdding(false)}>Cancel</Button>
            <Button
              size="sm"
              disabled={personId === NONE || addMut.isPending}
              onClick={() => addMut.mutate()}
            >
              {addMut.isPending ? 'Adding…' : 'Add contact'}
            </Button>
          </div>
        </div>
      )}

      {contactsQ.isLoading ? (
        <p className="text-muted-foreground text-sm">Loading contacts…</p>
      ) : contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No contacts yet. Add a person to give them portal access.
        </p>
      ) : (
        <div className="overflow-hidden rounded border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/40">
              <tr>
                <Th>Person</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Primary</Th>
                <Th>Portal</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {contacts.map((c) => (
                <tr key={c.id} className="hover:bg-muted/30">
                  <Td className="font-medium">
                    <Link
                      to={`/admin/hr/people/${c.person}`}
                      className="text-blue-600 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {c.person_name}
                    </Link>
                    {c.is_primary && (
                      <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">Primary</span>
                    )}
                  </Td>
                  <Td className="text-muted-foreground">
                    {c.person_email || <span className="text-muted-foreground/50">—</span>}
                  </Td>
                  <Td className="text-muted-foreground">
                    {c.role || <span className="text-muted-foreground/50">—</span>}
                  </Td>
                  <Td>
                    <input
                      type="checkbox"
                      checked={c.is_primary}
                      onChange={(e) =>
                        api.patch(`/partners/contacts/${c.id}/`, { is_primary: e.target.checked })
                          .then(() => qc.invalidateQueries({ queryKey: ['partners', 'contacts', partnerId] }))
                          .catch((err) => toast.error(extractErr(err)))
                      }
                      className="rounded"
                    />
                  </Td>
                  <Td>
                    <button
                      title={c.is_portal_active ? 'Disable portal access' : 'Enable portal access'}
                      onClick={() => togglePortalMut.mutate({ id: c.id, active: !c.is_portal_active })}
                      className="flex items-center gap-1.5"
                    >
                      <ContactPortalBadge status={c.portal_status} />
                    </button>
                  </Td>
                  <Td className="text-right">
                    <button
                      title="Remove contact"
                      className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                      onClick={() => confirm(
                        `${c.person_name} as a contact`,
                        () => deleteMut.mutate(c.id),
                      )}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
